import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  OnChanges,
  OnDestroy,
  ViewChild,
} from '@angular/core';
import * as echarts from 'echarts/core';
import { GaugeChart } from 'echarts/charts';
import { AriaComponent } from 'echarts/components';
import { SVGRenderer } from 'echarts/renderers';
import { ECBasicOption } from 'echarts/types/dist/shared';
import {
  ECHARTS_BASE_OPTIONS,
  ECHARTS_GENERATIVE_THEME,
  ECHARTS_GENERATIVE_THEME_NAME,
} from '@shared/constants/custom-report-theme.constants';
import { IChatV2ComponentChunk } from '@shared/interfaces/ai-executor.interface';
import { ScoreRangeColors, ScoreRanges, scoreRangeLabels } from '../../../../constants/charts.constants';
import { EchartsService } from '../../../../services/echarts.service';

const GAUGE_TRACK_COLOR: string = '#EEEEEE';

@Component({
  selector: 'app-ai-chat-gauge-chart',
  templateUrl: './ai-chat-gauge-chart.component.html',
  styleUrls: ['./ai-chat-gauge-chart.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [EchartsService],
  imports: [CommonModule],
  standalone: true,
})
export class AiChatGaugeChartComponent implements AfterViewInit, OnChanges, OnDestroy {
  @Input() public chunk: IChatV2ComponentChunk;

  @ViewChild('chartTarget') private chartTarget: ElementRef<HTMLDivElement>;

  public title: string = '';
  public label: string = '';
  public unit: string = '';
  public value: number = 0;
  public min: number = 0;
  public max: number = 100;
  private chart: echarts.EChartsType;

  constructor(
    private changeDetectorRef: ChangeDetectorRef,
    private echartsService: EchartsService,
  ) {}

  public ngAfterViewInit(): void {
    this.initChart();
  }

  public ngOnChanges(): void {
    if (this.chart) {
      this.initChart();
    }
  }

  public ngOnDestroy(): void {
    this.echartsService.disposeAll();
  }

  public static serialize(data: Record<string, unknown>): string {
    const title = (data['title'] as string) ?? '';
    const label = (data['label'] as string) ?? '';
    const value = (data['value'] as number) ?? 0;
    const min = (data['min'] as number) ?? 0;
    const max = (data['max'] as number) ?? 100;
    const unit = (data['unit'] as string) ?? '';

    const heading = [title, label].filter(Boolean).join(' - ');
    const summary = `${value}${unit} (${min}–${max})`;

    return heading ? `${heading}: ${summary}` : summary;
  }

  private getGaugeColor(value: number, min: number, max: number): string {
    const pct = max > min ? ((value - min) / (max - min)) * 100 : 0;
    if (pct >= ScoreRanges[scoreRangeLabels.high][0]) {
      return ScoreRangeColors[scoreRangeLabels.high];
    }
    if (pct >= ScoreRanges[scoreRangeLabels.mid][0]) {
      return ScoreRangeColors[scoreRangeLabels.mid];
    }
    return ScoreRangeColors[scoreRangeLabels.low];
  }

  private initChart(): void {
    if (!this.chartTarget) {
      return;
    }

    const data = this.chunk?.data ?? {};
    this.value = (data['value'] as number) ?? 0;
    this.min = (data['min'] as number) ?? 0;
    this.max = (data['max'] as number) ?? 100;
    this.title = (data['title'] as string) ?? '';
    this.label = (data['label'] as string) ?? '';
    this.unit = (data['unit'] as string) ?? '';

    const gaugeColor = this.getGaugeColor(this.value, this.min, this.max);
    const detailFormatter = this.unit ? `{value}${this.unit}` : '{value}';

    this.changeDetectorRef.detectChanges();

    echarts.use([GaugeChart, AriaComponent, SVGRenderer]);
    echarts.registerTheme(ECHARTS_GENERATIVE_THEME_NAME, ECHARTS_GENERATIVE_THEME);

    this.chart = this.echartsService.initialize(this.chartTarget.nativeElement, ECHARTS_GENERATIVE_THEME_NAME);

    const option: ECBasicOption = {
      ...ECHARTS_BASE_OPTIONS,
      aria: { decal: { show: false } },
      series: [
        {
          type: 'gauge',
          color: gaugeColor,
          startAngle: 225,
          endAngle: -45,
          min: this.min,
          max: this.max,
          progress: {
            show: true,
            width: 20,
            itemStyle: { color: gaugeColor },
          },
          axisLine: {
            lineStyle: {
              width: 20,
              color: [[1, GAUGE_TRACK_COLOR]],
            },
          },
          pointer: { show: false },
          axisTick: { show: false },
          splitLine: { show: false },
          axisLabel: { show: false },
          detail: {
            valueAnimation: false,
            fontSize: 32,
            fontWeight: 'bold',
            formatter: detailFormatter,
            offsetCenter: [0, '10%'],
            color: gaugeColor,
          },
          title: {
            offsetCenter: [0, '42%'],
            fontSize: 13,
            color: '#666666',
          },
          data: [{ value: this.value, name: this.label }],
        },
      ],
    };

    this.chart.setOption(option);
  }
}
